import React from 'react';
import Dialog from 'carbon-react/lib/components/dialog';
import Typography from 'carbon-react/lib/components/typography';
import { useIntl } from 'react-intl';

import * as styles from './insights-modal.scss';

const NEUTRAL_SCORE = 50;

interface HealthScoreCalculationDialogProps {
  isOpen: boolean;
  score: number;
  scoreDeltas: number[];
  onClose: () => void;
}

export const HealthScoreCalculationDialog = ({
  isOpen,
  score,
  scoreDeltas,
  onClose
}: HealthScoreCalculationDialogProps) => {
  const intl = useIntl();
  const hasNonZeroScoreDelta = scoreDeltas.some(delta => delta !== 0);

  return (
    <Dialog
      open={isOpen}
      onCancel={onClose}
      size="medium-small"
      topModalOverride
      className={styles.healthScoreCalculationModal}
      title={intl.formatMessage({
        id: 'aiInsights.healthScoreCalculation.title',
        defaultMessage: 'Health score calculation'
      })}
      contentPadding={{ p: 3 }}
      data-element="health-score-calculation-dialog"
    >
      <div className={styles.calculationContent}>
        <Typography fontSize="14px" fontWeight="500" lineHeight="150%" mb={0}>
          {intl.formatMessage({
            id: 'aiInsights.healthScoreCalculation.howYourScoreIsCalculated',
            defaultMessage: 'How your score is calculated'
          })}
        </Typography>
        <Typography fontSize="14px" fontWeight="400" lineHeight="150%" mb={0} color="rgba(0, 0, 0, 0.65)">
          {intl.formatMessage({
            id: 'aiInsights.healthScoreCalculation.explanation',
            defaultMessage:
              'All scores start at 50 (neutral) on a scale of 0-100. Positive insights increase the score; negative insights decrease it.'
          })}
          {hasNonZeroScoreDelta && (
            <>
              <br />
              <br />
              {intl.formatMessage({
                id: 'aiInsights.healthScoreCalculation.scoreFormulaPrefix',
                defaultMessage: 'Your score is:'
              })}{' '}
              {NEUTRAL_SCORE}
              {scoreDeltas.map((delta, index) => (
                <React.Fragment key={`${index}-${delta}`}>
                  {` ${delta >= 0 ? '+' : '−'} ${Math.abs(delta)}`}
                </React.Fragment>
              ))}
              {' = '}
              <span className={styles.calculationResult}>{score}</span>
            </>
          )}
        </Typography>
      </div>
    </Dialog>
  );
};
